import React, { useEffect, useState } from "react";
import {
  AcademyQuizCircleRing,
  AcademyQuizMesh,
  BadgeIcon,
  IncorrectAnswerIcon,
  RightAnswerIcon,
} from "../../../icons/academy-icons";
import Button from "../../common/button";
import {
  useLazyGetNextEpisodeQuery,
  usePostQuizResultMutation,
} from "../../../store/api/academyApi";
import { useLocation, useNavigate, useParams } from "react-router-dom";

interface QuizProps {
  quizData: any;
  episodeData: any;
}

const QuizOptionStyle = {
  correct: "!text-black bg-[#6EE755]",
  incorrect: "text-white bg-[#F36161]",
};

const Quiz: React.FC<QuizProps> = ({ quizData, episodeData }) => {
  const { episode_id } = useParams<any>();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState(0);
  const [triggerQuizComplete] = usePostQuizResultMutation();
  const [xp, setXP] = useState(0);
  const [answers, setAnswers] = useState(
    new Array(quizData?.questions.length).fill(0),
  );
  const currentQuestion = quizData?.questions[currentQuestionIndex];
  const navigate = useNavigate();

  const [getNextEpisode] = useLazyGetNextEpisodeQuery();
  const [nextEpisodeData, setNextEpisodeData] = useState<any>(null);

  const getNextEpisodeData = async () => {
    try {
      const response = await getNextEpisode({
        episodeId: episode_id,
      })?.unwrap();
      setNextEpisodeData(response.data);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    getNextEpisodeData();
  }, []);

  const handleOptionClick = (option: any, answer: any, index: number) => {
    if (!showAnswer) {
      setSelectedOption(option);
      setShowAnswer(true);
      if (option === answer) {
        setCorrectAnswers(correctAnswers + 1);
        // set particular value to 1 if correct
        setAnswers([
          ...answers.slice(0, index),
          1,
          ...answers.slice(index + 1),
        ]);
      } else {
        setWrongAnswers(wrongAnswers + 1);
        // set particular value to 2 if wrong
        setAnswers([
          ...answers.slice(0, index),
          2,
          ...answers.slice(index + 1),
        ]);
      }
    }
  };

  const submitQuiz = async () => {
    try {
      const response = await triggerQuizComplete({
        quizId: quizData._id,
        body: {
          quiz_id: quizData._id,
          episode_id,
          total_questions: quizData.questions.length,
          correct_answers: correctAnswers,
          incorrect_answers: wrongAnswers,
          un_attempted_questions:
            quizData.questions.length - correctAnswers - wrongAnswers,
        },
      })?.unwrap();
      if (response?.success) {
        setXP(response.data.xp_granted);
        setQuizComplete(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleNextQuestion = () => {
    setSelectedOption(null);
    setShowAnswer(false);
    if (currentQuestionIndex < quizData?.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      submitQuiz();
    }
  };

  const handleRetake = () => {
    setCurrentQuestionIndex(0);
    setSelectedOption(null);
    setShowAnswer(false);
    setQuizComplete(false);
    setCorrectAnswers(0);
    setWrongAnswers(0);
    setAnswers(new Array(quizData?.questions.length).fill(0));
  };

  const location = useLocation();

  const handleNextEpisode = () => {
    if (nextEpisodeData?.next_episode_id) {
      navigate(
        location?.pathname?.split("/").slice(0, -2).join("/") +
          "/" +
          nextEpisodeData?.module_id +
          "/" +
          nextEpisodeData?.next_episode_id,
      );
    } else {
      navigate(location?.pathname?.split("/").slice(0, -2).join("/"));
    }
  };

  return (
    <div className="w-full">
      <div className="flex gap-2">
        <div className="flex flex-col md:w-2/3">
          <div className="flex justify-between items-center bg-[#262626] text-white py-4 px-6 rounded-t-xl">
            <h1 className="text-xl font-bold">{episodeData?.title}</h1>
            <p className="">Complete the quiz to earn XP</p>
          </div>
          {quizComplete ? (
            <>
              <div className="w-full bg-[#090909] h-fit relative rounded-b-xl">
                <div className="absolute">
                  <AcademyQuizMesh />
                </div>
                <div className="z-1 relative flex flex-col items-center w-full h-[500px]">
                  <h1 className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-text h-fit pt-20 pb-4">
                    Congratulations!
                  </h1>
                  <h3 className="text-lg w-fit h-30 text-white font-bold">
                    {episodeData.title + " Quiz Completed"}{" "}
                  </h3>
                  <div className="flex items-center py-20 gap-2">
                    {xp !== 0 ? (
                      <>
                        <img src="/assets/icons/star-red.png" className="" />
                        <p className="text-white text-3xl font-semibold">
                          {xp} XP
                        </p>
                      </>
                    ) : (
                      <></>
                    )}
                  </div>
                  <div className="flex gap-4">
                    <Button
                      customClasses="!bg-[#262626] text-white py-3 px-8 rounded-xl font-semibold"
                      onClick={handleRetake}
                    >
                      <p>Retake Quiz</p>
                    </Button>
                    {nextEpisodeData?.next_episode_id && (
                      <Button
                        customClasses="text-white py-3 px-8 rounded-xl font-semibold"
                        onClick={handleNextEpisode}
                      >
                        <p>Go to Next Episode</p>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="w-full bg-[#090909] h-fit relative rounded-b-xl">
              <div className="absolute">
                <AcademyQuizMesh />
              </div>
              <div className="z-1 relative flex flex-col items-center w-full h-fit">
                <h3 className="text-3xl w-fit h-30 text-white py-20 px-4 font-bold text-center">
                  {currentQuestion?.text}
                </h3>
                <ul className="w-full px-12">
                  {currentQuestion?.options.map(
                    (option: any, index: number) => (
                      <li
                        key={index as any}
                        onClick={() =>
                          handleOptionClick(
                            option,
                            currentQuestion?.correct_option,
                            currentQuestionIndex,
                          )
                        }
                        className={`bg-[#262626] text-white px-8 py-4 rounded-md w-full my-4 flex justify-between items-center font-semibold cursor-pointer
                      ${
                        showAnswer
                          ? option === currentQuestion?.correct_option
                            ? QuizOptionStyle.correct
                            : option === selectedOption
                              ? QuizOptionStyle.incorrect
                              : "text-white"
                          : "text-black"
                      }`}
                      >
                        <p>{option}</p>
                        {showAnswer ? (
                          option === currentQuestion?.correct_option ? (
                            <RightAnswerIcon />
                          ) : option === selectedOption ? (
                            <IncorrectAnswerIcon />
                          ) : (
                            <AcademyQuizCircleRing />
                          )
                        ) : (
                          <AcademyQuizCircleRing />
                        )}
                      </li>
                    ),
                  )}
                </ul>
                <div className="border-t-[1px] border-[#404040] w-full my-2"></div>
                <div className="h-5 flex items-center justify-between px-10 pt-4 pb-8 w-full">
                  <div className="w-4/5 bg-gray-700 rounded-lg h-3">
                    <div
                      className="bg-gradient-text h-3 rounded-lg"
                      style={{
                        width: `${(correctAnswers * 100) / quizData?.questions.length}%`,
                      }}
                    ></div>
                  </div>
                  <div className="flex items-center">
                    <img src="/assets/icons/star-red.png" className="w-6 h-6" />
                    <p className="text-white font-semibold">
                      {correctAnswers * 10} XP
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {quizComplete ? (
            <></>
          ) : (
            <div className="flex justify-end gap-2 py-6">
              <Button
                customClasses={`bg-transparent ${showAnswer ? "!text-[#808080]" : "!text-[#090909]"} hover:bg-transparent font-semibold py-3 px-8`}
                onClick={handleNextQuestion}
                disabled={showAnswer}
              >
                <p className="!text-black">Skip Question</p>
              </Button>
              <Button
                customClasses={`py-3 px-8 rounded-xl ${showAnswer ? "cursor-pointer" : "!cursor-not-allowed"}`}
                onClick={handleNextQuestion}
                disabled={!showAnswer}
              >
                {currentQuestionIndex < quizData?.questions.length - 1
                  ? "Next Question"
                  : "Complete Quiz"}
              </Button>
            </div>
          )}
        </div>
        <div className="md:w-1/3 w-full h-fit bg-white rounded-lg p-4">
          <div className="w-full flex gap-2 items-center p-2">
            <BadgeIcon width={18} height={24} />
            <h2 className="text-xl font-semibold">Number of Questions</h2>
          </div>
          <div className="grid md:grid-cols-4 sm:grid-cols-3 gap-4">
            {answers.map((ans: any, index: number) => {
              return (
                <div
                  key={index}
                  className={`h-8 flex justify-center items-center p-4 rounded text-center font-semibold text-black ${
                    answers[index] === 0
                      ? "bg-[#F5F5F5]"
                      : answers[index] === 1
                        ? "bg-[#DBF9D5]"
                        : "bg-[#FFE5E4]"
                  }`}
                >
                  {index + 1}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
